// Library methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { SelectChangeEvent } from "@mui/material/Select";
import TextInputField from "src/components/shared/inputs/TextInputField/TextInputField";
import SelectDropdown from "src/components/shared/inputs/SelectDropdown/SelectDropdown";
import FormField from "../FormField/FormField";
import ExamTypePicker from "../ExamTypePicker/ExamTypePicker";
import WorklistTiming from "../WorklistTiming/WorklistTiming";
import PollInterval from "../PollInterval/PollInterval";

// Utils
import { parseAllowedRemoteHosts } from "src/utils/emr/allowedRemoteHostsHelper";
import {
  ASSOCIATION_TIMEOUT_SECONDS_MAX,
  ASSOCIATION_TIMEOUT_SECONDS_MIN,
  ConnectionFormValues,
  DICOM_AE_TITLE_MAX_LENGTH,
  isValidExportFrom,
} from "src/utils/emr/connectionConfigHelper";

// Types
import {
  EMRDicomStoreProfile,
  EMRExamType,
  EMRHubConnectionSourceKind,
} from "src/services/emr/types";

const ALLOWED_REMOTE_HOSTS_DISALLOWED_CHARACTERS = /[^0-9a-fA-F:./,\s]/g;
const DIGITS_ONLY = /[^0-9]/g;

type Option<T extends string> = { displayName: string; value: T };

const findValue = <T extends string>(
  options: Array<Option<T>>,
  event: SelectChangeEvent<any>
) =>
  options.find((candidate) => candidate.displayName === event.target.value)
    ?.value;

const findDisplayName = <T extends string>(
  options: Array<Option<T>>,
  value: T
) => options.find((option) => option.value === value)?.displayName ?? "";

type ConnectionFormType = {
  idPrefix: string;
  name: string;
  onNameChange: (name: string) => void;
  sourceKind: EMRHubConnectionSourceKind;
  values: ConnectionFormValues;
  onValuesChange: (values: Partial<ConnectionFormValues>) => void;
  isDuplicatePath: boolean;
  examTypeOptions: Array<EMRExamType>;
  showProfile: boolean;
};

const ConnectionForm = ({
  idPrefix,
  name,
  onNameChange,
  sourceKind,
  values,
  onValuesChange,
  isDuplicatePath,
  examTypeOptions,
  showProfile,
}: ConnectionFormType) => {
  const { t } = useTranslation();

  const profileOptions = useMemo<Array<Option<EMRDicomStoreProfile>>>(
    () => [
      { displayName: t("Generic (FORUM)"), value: "Generic" },
      { displayName: t("Topcon Harmony"), value: "TopconHarmony" },
    ],
    [t]
  );

  const parsedAllowedRemoteHosts = useMemo(
    () => parseAllowedRemoteHosts(values.allowedRemoteHosts),
    [values.allowedRemoteHosts]
  );

  const isSharedFolder = sourceKind === "SharedFolder";
  const isDicomListener = sourceKind === "DicomListener";
  const isQueryRetrieve = sourceKind === "DicomQueryRetrieve";
  const isWorklist = sourceKind === "Worklist";
  const isCsvWorklist = isWorklist && values.worklistMode === "CsvFolder";
  const isDicomStore = sourceKind === "DicomStore";
  const hasRemoteArchive =
    isQueryRetrieve || (isWorklist && !isCsvWorklist) || isDicomStore;
  const canFilterExamTypes =
    examTypeOptions.length > 0 &&
    (isDicomListener || isQueryRetrieve || (isWorklist && !isCsvWorklist));

  const associationTimeout = Number(values.associationTimeoutSeconds);
  const isAssociationTimeoutInvalid =
    values.associationTimeoutSeconds !== "" &&
    (associationTimeout < ASSOCIATION_TIMEOUT_SECONDS_MIN ||
      associationTimeout > ASSOCIATION_TIMEOUT_SECONDS_MAX);

  return (
    <Grid display="flex" flexDirection="column" p="16px" gap="12px">
      <FormField label={t("Name")} htmlFor={`${idPrefix}-name-input`} required>
        <TextInputField
          id={`${idPrefix}-name-input`}
          autoFocus
          required
          placeholder={t("Enter a value")}
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          maxLength={100}
        />
      </FormField>

      {(isSharedFolder || isCsvWorklist) && (
        <FormField
          label={t("Folder")}
          htmlFor={`${idPrefix}-path-input`}
          required
        >
          <TextInputField
            id={`${idPrefix}-path-input`}
            required
            placeholder={
              isCsvWorklist ? "C:\\clinic\\worklist" : "C:\\testFolder\\test"
            }
            value={values.path}
            onChange={(event) => onValuesChange({ path: event.target.value })}
            maxLength={260}
            error={isDuplicatePath}
            helperText={
              isDuplicatePath
                ? t("An EMR connection already exists for this path.")
                : isCsvWorklist
                  ? t("The folder the EMR drops its YYYY-MM-DD*.csv files in.")
                  : undefined
            }
          />
        </FormField>
      )}

      {canFilterExamTypes && (
        <Box display="flex" flexDirection="column" gap="6px">
          <Typography fontSize="12px">{t("Exams to bring in")}</Typography>
          <ExamTypePicker
            options={examTypeOptions}
            value={values.examTypes}
            onChange={(examTypes) => onValuesChange({ examTypes })}
          />
        </Box>
      )}

      {isDicomListener && (
        <>
          <Box display="flex" gap="8px">
            <FormField
              label={t("Machine AE title")}
              htmlFor={`${idPrefix}-calling-ae-title-input`}
              required
            >
              <TextInputField
                id={`${idPrefix}-calling-ae-title-input`}
                required
                placeholder={t("e.g. CIRRUS01")}
                value={values.allowedCallingAeTitle}
                onChange={(event) =>
                  onValuesChange({ allowedCallingAeTitle: event.target.value })
                }
                maxLength={DICOM_AE_TITLE_MAX_LENGTH}
              />
            </FormField>

            <FormField
              label={t("Machine IP address")}
              htmlFor={`${idPrefix}-allowed-remote-hosts-input`}
            >
              <TextInputField
                id={`${idPrefix}-allowed-remote-hosts-input`}
                placeholder={t("Optional, e.g. 10.0.0.25")}
                value={values.allowedRemoteHosts}
                onChange={(event) =>
                  onValuesChange({
                    allowedRemoteHosts: event.target.value.replace(
                      ALLOWED_REMOTE_HOSTS_DISALLOWED_CHARACTERS,
                      ""
                    ),
                  })
                }
                maxLength={1500}
                error={parsedAllowedRemoteHosts.invalid.length > 0}
                helperText={
                  parsedAllowedRemoteHosts.invalid.length > 0
                    ? t("Not a valid IP address or CIDR range.")
                    : undefined
                }
              />
            </FormField>
          </Box>

          <Box display="flex" gap="8px">
            <FormField
              label={t("Hub AE title")}
              htmlFor={`${idPrefix}-ae-title-input`}
              required
            >
              <TextInputField
                id={`${idPrefix}-ae-title-input`}
                required
                placeholder={t("EMRHUB")}
                value={values.aeTitle}
                onChange={(event) =>
                  onValuesChange({ aeTitle: event.target.value })
                }
                maxLength={DICOM_AE_TITLE_MAX_LENGTH}
              />
            </FormField>

            <FormField
              label={t("Hub port")}
              htmlFor={`${idPrefix}-port-input`}
              required
            >
              <TextInputField
                id={`${idPrefix}-port-input`}
                required
                placeholder={t("104")}
                value={values.port}
                onChange={(event) =>
                  onValuesChange({
                    port: event.target.value.replace(DIGITS_ONLY, ""),
                  })
                }
                maxLength={5}
              />
            </FormField>
          </Box>
        </>
      )}

      {hasRemoteArchive && (
        <>
          <Box display="flex" gap="8px">
            <FormField
              label={t("Host / IP")}
              htmlFor={`${idPrefix}-host-input`}
              required
            >
              <TextInputField
                id={`${idPrefix}-host-input`}
                required
                placeholder={t("10.0.0.20")}
                value={values.host}
                onChange={(event) =>
                  onValuesChange({ host: event.target.value })
                }
                maxLength={260}
              />
            </FormField>

            <FormField
              label={t("Port")}
              htmlFor={`${idPrefix}-remote-port-input`}
              required
            >
              <TextInputField
                id={`${idPrefix}-remote-port-input`}
                required
                placeholder={t("104")}
                value={values.remotePort}
                onChange={(event) =>
                  onValuesChange({
                    remotePort: event.target.value.replace(DIGITS_ONLY, ""),
                  })
                }
                maxLength={5}
              />
            </FormField>
          </Box>

          <Box display="flex" gap="8px">
            <FormField
              label={t("Remote AE title")}
              htmlFor={`${idPrefix}-remote-ae-title-input`}
              required
            >
              <TextInputField
                id={`${idPrefix}-remote-ae-title-input`}
                required
                placeholder="PACS"
                value={values.remoteAeTitle}
                onChange={(event) =>
                  onValuesChange({ remoteAeTitle: event.target.value })
                }
                maxLength={DICOM_AE_TITLE_MAX_LENGTH}
              />
            </FormField>

            <FormField
              label={t("Hub AE title")}
              htmlFor={`${idPrefix}-local-ae-title-input`}
              required
            >
              <TextInputField
                id={`${idPrefix}-local-ae-title-input`}
                required
                placeholder={t("EMRHUB")}
                value={values.localAeTitle}
                onChange={(event) =>
                  onValuesChange({ localAeTitle: event.target.value })
                }
                maxLength={DICOM_AE_TITLE_MAX_LENGTH}
              />
            </FormField>
          </Box>
        </>
      )}

      {isQueryRetrieve && (
        <PollInterval
          idPrefix={idPrefix}
          minutes={values.pollIntervalMinutes}
          onChange={(pollIntervalMinutes) =>
            onValuesChange({ pollIntervalMinutes })
          }
        />
      )}

      {isWorklist && (
        <>
          {!isCsvWorklist && (
            <FormField
              label={t("Scheduled station AE title")}
              htmlFor={`${idPrefix}-scheduled-station-ae-title-input`}
            >
              <TextInputField
                id={`${idPrefix}-scheduled-station-ae-title-input`}
                placeholder={t("Optional")}
                value={values.scheduledStationAeTitle}
                onChange={(event) =>
                  onValuesChange({
                    scheduledStationAeTitle: event.target.value,
                  })
                }
                maxLength={DICOM_AE_TITLE_MAX_LENGTH}
              />
            </FormField>
          )}

          <Box display="flex" flexDirection="column" gap="6px">
            <Typography fontSize="12px">
              {t("When should the worklist be read?")}
            </Typography>
            <WorklistTiming
              idPrefix={idPrefix}
              value={values.timing}
              onChange={(timing) => onValuesChange({ timing })}
            />
          </Box>
        </>
      )}

      {isDicomStore && (
        <>
          <Box display="flex" gap="8px">
            {showProfile && (
              <FormField
                label={t("Archive")}
                labelId={`${idPrefix}-profile-label`}
                required
              >
                <SelectDropdown
                  id={`${idPrefix}-profile-dropdown`}
                  labelId={`${idPrefix}-profile-label`}
                  dropdownOptions={profileOptions}
                  value={findDisplayName(profileOptions, values.profile)}
                  handleChange={(event) => {
                    const value = findValue(profileOptions, event);
                    if (value) onValuesChange({ profile: value });
                  }}
                />
              </FormField>
            )}

            <FormField
              label={t("Timeout (seconds)")}
              htmlFor={`${idPrefix}-association-timeout-input`}
              required
            >
              <TextInputField
                id={`${idPrefix}-association-timeout-input`}
                required
                placeholder={t("30")}
                value={values.associationTimeoutSeconds}
                onChange={(event) =>
                  onValuesChange({
                    associationTimeoutSeconds: event.target.value.replace(
                      DIGITS_ONLY,
                      ""
                    ),
                  })
                }
                maxLength={3}
                error={isAssociationTimeoutInvalid}
                helperText={t("Between 5 and 300.")}
              />
            </FormField>
          </Box>

          <FormField
            label={t("Send Reports From")}
            htmlFor={`${idPrefix}-export-from-input`}
          >
            <TextInputField
              id={`${idPrefix}-export-from-input`}
              placeholder={t("YYYY-MM-DD")}
              value={values.exportFrom}
              onChange={(event) =>
                onValuesChange({
                  exportFrom: event.target.value.replace(/[^0-9-]/g, ""),
                })
              }
              maxLength={10}
              error={!isValidExportFrom(values.exportFrom)}
              helperText={
                isValidExportFrom(values.exportFrom)
                  ? t(
                      "Optional. Only exams created on or after this date are sent. Defaults to the hub's install date."
                    )
                  : t("Enter a date as YYYY-MM-DD.")
              }
            />
          </FormField>
        </>
      )}
    </Grid>
  );
};

export default ConnectionForm;
